import { LengthFormat, UnitSystem } from "../index";

/**
 * Formats numbers to 2 decimals
 * There's also formatNumberOneDecimal that can be used
 */
export function formatNumber(num: number | string) {
  return ((Number(num) * 100) / 100).toFixed(2);
}

export const lengthConverter = (value: string) =>
  Math.round(Number(value.replace(",", ".")) * 100) / 100;

const INCHES_PER_FOOT = 12;
const METRES_PER_INCH = 0.0254;
const METRES_PER_FOOT = METRES_PER_INCH * INCHES_PER_FOOT;
const MILLIMETRES_PER_METRE = 1000;

// Covers both imperial length notations (feet-inches: ', ", -; decimal
// feet: the "ft" suffix) in one mask, since the input mask only needs to
// admit characters the formatter can produce, not enforce which format.
export const LENGTH_INPUT_REGEX: Record<UnitSystem, RegExp> = {
  metric: /^[0-9,.]*$/,
  imperial: /^[0-9,.'"\s\-ftFT]*$/,
};

export const THICKNESS_INPUT_REGEX: Record<UnitSystem, RegExp> = {
  metric: /^[0-9,.]*$/,
  imperial: /^[0-9,."]*$/,
};

// Rounds to the nearest whole inch first (as a single total-inch value),
// then splits into feet/inches — this is what makes the 12"-carry correct
// for free (e.g. 7.62m -> 300in -> 25'-0", never 24'-12").
const formatFeetInches = (metres: number): string => {
  const totalInches = Math.round(metres / METRES_PER_INCH);
  const feet = Math.floor(totalInches / INCHES_PER_FOOT);
  const inches = totalInches - feet * INCHES_PER_FOOT;
  if (feet === 0) {
    return `${inches}"`;
  }
  return `${feet}'-${inches}"`;
};

// Site plans conventionally use decimal feet (engineering scale); floor
// plans/elevations use feet-inches (architectural scale) — formatFeetInches
// above. Trailing zeros are trimmed (10 ft, 24.5 ft), matching how these
// values actually appear on real drawings.
const formatDecimalFeet = (metres: number): string => {
  const feet = Math.round((metres / METRES_PER_FOOT) * 100) / 100;
  return `${feet} ft`;
};

// Millimetres are the standard unit on construction drawings, so they're
// written as a whole number (5050, not 5050.00) — a millimetre is already
// finer than anything these drawings are dimensioned to.
const formatMillimetres = (metres: number): string =>
  String(Math.round(metres * MILLIMETRES_PER_METRE));

const parseMillimetres = (value: string): number =>
  Math.round(Number(value.replace(",", ".")) || 0) / MILLIMETRES_PER_METRE;

// The formats each unit system can actually render, most-common first (also
// the order of the <option>s in the settings dropdowns).
export const LENGTH_FORMATS_BY_UNIT_SYSTEM: Record<
  UnitSystem,
  readonly LengthFormat[]
> = {
  metric: ["meters", "millimeters"],
  imperial: ["feet-inches", "decimal-feet"],
};

// A project keeps whatever lengthFormat it was last given when its unit
// system changes, and 'meters' is the backward-compatible column default
// (see LengthFormat in core/index.ts). Either way the stored format can
// belong to the other system, so it's resolved back to the current
// system's default rather than rendering a notation it can't produce.
export const resolveLengthFormat = (
  unitSystem: UnitSystem,
  lengthFormat: LengthFormat,
): LengthFormat =>
  LENGTH_FORMATS_BY_UNIT_SYSTEM[unitSystem].includes(lengthFormat)
    ? lengthFormat
    : LENGTH_FORMATS_BY_UNIT_SYSTEM[unitSystem][0];

// The unit shown next to an editable input, where the notation itself can't
// carry it (imperial embeds ' "/ft in the value, so it has nothing left to
// indicate).
export const lengthUnitLabel = (
  unitSystem: UnitSystem,
  lengthFormat: LengthFormat = "meters",
): string | null => {
  if (unitSystem === "imperial") return null;
  return resolveLengthFormat(unitSystem, lengthFormat) === "millimeters"
    ? "mm"
    : "m";
};

// The "bare" value, with a unit embedded only where the notation itself
// requires it (imperial always needs ' "/ft to disambiguate). Used for
// editable inputs, which pair this with a separate unit indicator for
// metric only (imperial has nothing left to indicate).
export const formatLengthValue = (
  metres: number,
  unitSystem: UnitSystem,
  lengthFormat: LengthFormat = "meters",
): string => {
  const format = resolveLengthFormat(unitSystem, lengthFormat);
  if (unitSystem === "imperial") {
    return format === "decimal-feet"
      ? formatDecimalFeet(metres)
      : formatFeetInches(metres);
  }
  return format === "millimeters"
    ? formatMillimetres(metres)
    : formatNumber(metres);
};

// Full self-contained label for read-only canvas text (dimension lines,
// radius labels) — always includes a trailing unit for metric.
export const formatLength = (
  metres: number,
  unitSystem: UnitSystem,
  lengthFormat: LengthFormat = "meters",
): string => {
  const value = formatLengthValue(metres, unitSystem, lengthFormat);
  const unit = lengthUnitLabel(unitSystem, lengthFormat);
  return unit ? `${value} ${unit}` : value;
};

const parseFeetInches = (value: string): number => {
  const trimmed = value.trim();
  if (trimmed === "") return 0;

  if (trimmed.includes("'")) {
    const [feetPart, rest = ""] = trimmed.split("'");
    const feet = Number(feetPart.replace(",", ".")) || 0;
    const inchesPart = rest
      .replace('"', "")
      .replace(/^[\s-]+/, "")
      .trim();
    const inches = inchesPart ? Number(inchesPart.replace(",", ".")) || 0 : 0;
    return feet * METRES_PER_FOOT + inches * METRES_PER_INCH;
  }

  if (trimmed.includes('"')) {
    const inches = Number(trimmed.replace('"', "").replace(",", ".")) || 0;
    return inches * METRES_PER_INCH;
  }

  if (/^\d+(?:[.,]\d+)?-\d+(?:[.,]\d+)?$/.test(trimmed)) {
    const [feetStr, inchesStr] = trimmed.split("-");
    const feet = Number(feetStr.replace(",", ".")) || 0;
    const inches = Number(inchesStr.replace(",", ".")) || 0;
    return feet * METRES_PER_FOOT + inches * METRES_PER_INCH;
  }

  // Bare decimal number with no feet/inch marks means decimal feet.
  const feet = Number(trimmed.replace(",", ".")) || 0;
  return feet * METRES_PER_FOOT;
};

const parseDecimalFeet = (value: string): number => {
  const trimmed = value.trim().replace(/ft$/i, "").replace(/'$/, "").trim();
  if (trimmed === "") return 0;
  const feet = Number(trimmed.replace(",", ".")) || 0;
  return feet * METRES_PER_FOOT;
};

export const parseLength = (
  value: string,
  unitSystem: UnitSystem,
  lengthFormat: LengthFormat = "meters",
): number => {
  const format = resolveLengthFormat(unitSystem, lengthFormat);
  if (unitSystem === "imperial") {
    return format === "decimal-feet"
      ? parseDecimalFeet(value)
      : parseFeetInches(value);
  }
  return format === "millimeters"
    ? parseMillimetres(value)
    : lengthConverter(value);
};

// The "bare" value, mirroring formatLengthValue: imperial always embeds the
// inch mark, metric is a plain number paired with a separate unit indicator.
export const formatThicknessValue = (
  metres: number,
  unitSystem: UnitSystem,
  lengthFormat: LengthFormat = "meters",
): string => {
  if (unitSystem === "imperial") {
    return `${formatNumber(metres / METRES_PER_INCH)}"`;
  }
  return resolveLengthFormat(unitSystem, lengthFormat) === "millimeters"
    ? formatMillimetres(metres)
    : formatNumber(metres);
};

export const formatThickness = (
  metres: number,
  unitSystem: UnitSystem,
  lengthFormat: LengthFormat = "meters",
): string => {
  const value = formatThicknessValue(metres, unitSystem, lengthFormat);
  const unit = lengthUnitLabel(unitSystem, lengthFormat);
  return unit ? `${value} ${unit}` : value;
};

export const parseThickness = (
  value: string,
  unitSystem: UnitSystem,
  lengthFormat: LengthFormat = "meters",
): number => {
  if (unitSystem === "imperial") {
    return lengthConverter(value.replace('"', "")) * METRES_PER_INCH;
  }
  return resolveLengthFormat(unitSystem, lengthFormat) === "millimeters"
    ? parseMillimetres(value)
    : lengthConverter(value);
};
